"use client";

import React, { useState, useCallback, useEffect } from "react";
import { Release } from "@/types/release.types";
import { Artist } from "@/types/artist.types";
import Image from "next/image";
import { FaChevronLeft, FaChevronRight } from "@/components/icons";
import Link from "next/link";
import { cn } from "@heroui/react";
import useEmblaCarousel from "embla-carousel-react";
import { slugifyReleaseName } from "@/lib/artistUtils";

export default function ReleasesCarousel({
  releases,
  artist,
}: {
  releases: Release[];
  artist: Artist;
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 3,
    align: "start",
    containScroll: "keepSnaps",
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi]
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi]
  );
  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="relative w-full">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">
          {releases.map((release, index) => (
            <Link
              //How artist release URL looks like: /release/spiritual-driveby/az8h38aog7
              href={`/release/${slugifyReleaseName(release.title)}/${release.short_id}`}
              className="flex-[0_0_calc((100%-2rem)/3)]"
              key={`${artist.handle}-release-${release.id}`}
            >
              <div className="aspect-square relative w-full border-[1px] dark:border-line-strong rounded-md overflow-hidden">
                {!release.is_owner && (
                  <div className="absolute top-0 right-0 px-2 py-1 bg-opacity-70 bg-theme-neon text-on-accent text-xs z-50">
                    Appears on
                  </div>
                )}
                <Image
                  className="absolute h-full w-full object-cover"
                  src={release.image_url}
                  alt={`Cover Image of release - ${release.title}`}
                  fill
                  sizes="(max-width: 768px) 50vw, (max-width: 1024px) 20vw, 15vw"
                />
                <div className="absolute top-0 bg-dark-background w-full h-full opacity-40" />
                <div className="absolute top-0 bg-gradient-to-b from-transparent to-canvas w-full h-full" />
              </div>
              <div className="flex flex-col justify-center w-full lg:px-1 mt-2 text-center">
                <p className="text-md leading-4 text-sm text-center">
                  {release.title}
                </p>
                <p className="text-xxs">{release.label}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <button
        onClick={scrollPrev}
        disabled={prevBtnDisabled}
        aria-label="Previous releases"
        className={cn(
          "absolute left-0 top-1/2 -translate-y-1/2 p-3 text-ink bg-canvas/50 rounded-full hover:bg-canvas/80 transition-all",
          prevBtnDisabled
            ? "opacity-50 cursor-not-allowed"
            : "opacity-100 hover:scale-110"
        )}
      >
        <FaChevronLeft className="text-xl" />
      </button>

      <button
        onClick={scrollNext}
        disabled={nextBtnDisabled}
        aria-label="Next releases"
        className={cn(
          "absolute right-0 top-1/2 -translate-y-1/2 p-3 text-ink bg-canvas/50 rounded-full hover:bg-canvas/80 transition-all",
          nextBtnDisabled
            ? "opacity-50 cursor-not-allowed"
            : "opacity-100 hover:scale-110"
        )}
      >
        <FaChevronRight className="text-xl" />
      </button>

      {/* <div className="flex justify-center space-x-1 mt-2">
        {scrollSnaps.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={cn(
              "h-2 w-2 rounded-full border-[1px] border-ink transition-colors",
              selectedIndex === index ? "bg-surface-raised" : "bg-transparent"
            )}
          />
        ))}
      </div> */}
    </div>
  );
}
